import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useState } from 'react';
import { Box, Button, Radio, Menu, MenuItem } from '@mui/material';
import { useGridApiContext } from '@mui/x-data-grid';
import strings from '../Globalization';
import consts from '../consts';
import Model from '../models/model';
import Connection from '../services/Connection';
import util from '../util';
import { usePrerequisites } from '../contexts/Prerequisites';
import { useGridBase } from '../components/grid/GridBaseContext';

const { requiredDeviceType, ModelDeviceType, DeviceTypeMapper } = consts;
const interfaceNameMapper = {
	"userFile": "Users",
	"scopeFile": "Scopes",
	"physicianFile": "Physician"
}
const bodyMapper = {
	'EndoDry': {
		'physicianFile': {
			otherAction: 'notRequired',
		},
	},
	'EndoCabinet': {
		'physicianFile': {
			otherAction: 'notRequired',
		}
	},
	'RapidAER': {
		'physicianFile': {
			otherAction: 'notRequired',
		}
	},
	'ISA': {
		'physicianFile': {
			otherAction: 'notRequired',
		}
	},
	'DSD Horizon': {
		'physicianFile': {
			otherAction: 'notRequired',
		},
	}
}
const RadioButton = ({ id, row }) => {

	const { rowSelectionModel, onRowSelectionModel } = useGridBase();
	const handleOnClick = () => {
		if (rowSelectionModel.ids.size > 0 && rowSelectionModel.ids.has(row._id)) {
			onRowSelectionModel({
				...rowSelectionModel,
				ids: new Set(),
				data: {}
			});
		}
		else {
			onRowSelectionModel({
				...rowSelectionModel,
				ids: new Set([row._id]),
				data: row
			});
		}
	}

	return (
		<Box>
			<Radio
				checked={
					rowSelectionModel.ids.has(row._id)
				}
				onClick={handleOnClick}
				value={id}
				name="radio-buttons"
				slotProps={{ input: { 'aria-label': 'Select' } }}
			/>
		</Box>
	);
}

const CombinedExport = () => {
	const [anchorEl, setAnchorEl] = useState(null);
	const [{ csrfTokenMap }] = usePrerequisites();
	const { rowSelectionModel } = useGridBase();
	const open = Boolean(anchorEl);
	const handleClose = () => {
		setAnchorEl(null);
	};
	const handelExport = async (rowSelectionModel, exportType) => {
		if (rowSelectionModel.ids.size !== 0) {
			const { HuluMachineModelID = '', DeviceType = '', usersCount = 0, physicianCount = 0 } = rowSelectionModel.data;
			const otherAction = bodyMapper[ModelDeviceType[DeviceType]]?.[exportType]?.otherAction ?? 'handleExport';
			if (consts.EquipmentFamilyID.Reliance6500 === DeviceType && exportType === 'physicianFile') {
				util.showError(strings.noUserDataForExport, strings.Info, 'info');
				return;
			}
			let countCheck = 0;
			if (exportType === 'userFile') {
				countCheck = usersCount;
			} if (exportType === 'physicianFile') {
				countCheck = physicianCount;
			} if (exportType === 'scopeFile') {
				countCheck = rowSelectionModel.data[DeviceTypeMapper[DeviceType]]
			}
			if (!requiredDeviceType.includes(DeviceType) || otherAction === 'notRequired' || !countCheck) {
				const message = !countCheck ? strings.noUserDataForExport : strings.notApplicable;
				util.showError(message, strings.Info, 'info');
			} else {
				const action = `${Connection.EndoSetupScope}?v=${new Date().getTime()}&csrf-token=${csrfTokenMap.get('value')}`;
				const body = {
					action: 'otherAction',
					otherAction: 'handleExport',
					HuluMachineModelID: HuluMachineModelID,
					interfaceType: interfaceNameMapper[exportType],
					deviceType: DeviceTypeMapper[DeviceType]
				};
				util.dynamicFormSubmission({ body, action });
			}
		} else {
			util.showError(strings.ExportValidation, strings.Info, 'info');
		}
	}
	const handleClick = async (exportType) => {
		await handelExport(rowSelectionModel, exportType);
	}
	return (
		<div>
			<Button
				id="fade-button"
				aria-controls={open ? 'fade-menu' : undefined}
				aria-haspopup="true"
				aria-expanded={open ? 'true' : undefined}
				onClick={(event) => setAnchorEl(event.currentTarget)}
				variant='contained'
				disabled={util.checkUserRoleAccess(["huluadmin", "operator", "department"])}
			>
				<FileDownloadOutlinedIcon />
				{strings.ExportData}
			</Button>
			<Menu
				id="fade-menu"
				MenuListProps={{
					'aria-labelledby': 'fade-button',
				}}
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
			>
				<MenuItem onClick={(e) => handleClick('userFile')}>{strings.UserFile}</MenuItem>
				<MenuItem onClick={(e) => handleClick('scopeFile')}>{strings.ScopeFile}</MenuItem>
				<MenuItem onClick={(e) => handleClick('physicianFile')}>{strings.PhysicianFile}</MenuItem>
			</Menu>
		</div >
	)
}

export const model = new Model({
	name: 'EndoSetupEquipment',
	allowImport: false,
	disableDelete: true,
	disableAdd: true,
	disableExport: false,
	idProperty: '_id',
	readOnly: true,
	formTitleField: 'ScopeName',
	pageSize: 5,
	defaultSort: 'ModelName ASC',
	showGridRefreshBtn: true,
	gridRefreshInterval: 140000, //Long polling ms
	filter: [{ "operator": "$isExist", "property": "ModelId", 'isSwitch': true }, { operator: '$not', property: 'NoOfUnits', value: 0, type: 'number' }, { operator: "$not", property: "ModelName", value: "enspire 300 Series AER", type: 'string' }, { operator: "$not", property: "ModelName", value: "System 1 Endo 2", type: "string" }],
	gridToolbarCustomActions: [
		{
			Component: CombinedExport
		},
	],
	columns: [
		{ header: 'Model', field: 'ModelName', width: 120 },
		{ header: 'NoOfUnits', field: 'NoOfUnits', width: 120, align: 'right', headerAlign: 'right' },
		{ header: 'AllowMultiScopes', field: 'AllowMultiScopes', width: 150, type: 'bool' },
		{ header: 'Disinfectant', field: 'ChemistryName', width: 150 },
		{ header: 'DetergentName', field: 'DetergentName', width: 140 },
		{ header: 'DoubleWash', field: 'DoubleWash', width: 120, type: 'bool' },
		{ header: 'StdCycle', field: 'ConfigUsageStd', width: 150 },
		{ header: 'AlcoholCycle', field: 'ConfigUsageAlc', width: 150 },
		{ header: 'UseAlcohol', field: 'UseAlcohol', width: 130, type: 'bool' },
		{ header: 'Active', field: 'IsActive', width: 120, type: 'bool' },
		{
			field: 'Select', width: 150, type: 'action', isNotExportField: true, renderCell: (param) => <RadioButton id={param.id} row={param.row} value={param.row.ModelName} />
		}
	]
});
export const EndoEquipmentModel = model;
export default model.createFormBase();